import type { BillingInfoResponse } from '../../repositories';
import { PaymentMethodsEnum } from '../../types';

// values for account status coming from  https://myaccount-api.l1.ctct.net/v2/api-docs
enum AccountStatus {
  Trial = 'T',
  Open = 'O',
  Canceled = 'C',
  TrailEnd = 'E',
}

export class BillingInfo {
  private readonly customerInfo: BillingInfoResponse['customer_info'];

  private readonly paymentInfo: BillingInfoResponse['payment_info'];

  private readonly overview: BillingInfoResponse['overview'];

  constructor(billingInfoApi: BillingInfoResponse) {
    this.customerInfo = billingInfoApi.customer_info;
    this.paymentInfo = billingInfoApi.payment_info;
    this.overview = billingInfoApi.overview;
  }

  // Customer Info Getters
  public getFirstName() {
    return this.customerInfo.first_name ?? '';
  }

  public getLastName() {
    return this.customerInfo.last_name ?? '';
  }

  public getAddress1() {
    return this.customerInfo.address1 ?? '';
  }

  public getAddress2() {
    return this.customerInfo.address2 ?? '';
  }

  public getCompanyName() {
    return this.customerInfo.company_name ?? '';
  }

  public getCity() {
    return this.customerInfo.city ?? '';
  }

  public getState() {
    return this.customerInfo.state ?? '';
  }

  public getCountry() {
    return this.customerInfo.country ?? '';
  }

  public getZipCode() {
    if (!this.customerInfo.postal_code) return '';

    return this.customerInfo.postal_code4
      ? `${this.customerInfo.postal_code}-${this.customerInfo.postal_code4}`
      : this.customerInfo.postal_code;
  }

  public getPostalCode() {
    return this.customerInfo.postal_code ?? '';
  }

  public getPostalCode4() {
    return this.customerInfo.postal_code4 ?? '';
  }

  public getNonUsTaxExemptionNumber() {
    return this.customerInfo.non_us_tax_exemption_number ?? '';
  }

  public getUsTaxExemptionNumber() {
    return this.customerInfo.us_tax_exemption_number;
  }

  public getTaxExemptionAgreement() {
    // we don't save this value on the BE;
    // if user has a non us tax number -- agreement should be true
    if (this.getNonUsTaxExemptionNumber()) {
      return true;
    }

    return this.customerInfo.tax_exemption_agreement ?? false;
  }

  public getEmail() {
    return this.customerInfo.email ?? '';
  }

  public getPhone() {
    return this.customerInfo.phone ?? '';
  }

  public getBillingLetterOption() {
    return this.customerInfo.billing_letter_option ?? 'Y';
  }

  public getTaxExemptionExpiration() {
    return this.customerInfo.tax_exemption_expiration;
  }

  // Payment Info Getters
  public getPaymentType() {
    return this.paymentInfo.payment_type;
  }

  public getCreditCardInfo() {
    return this.paymentInfo.credit_card_info;
  }

  public getDigitalWalletInfo() {
    return this.paymentInfo.credit_card_info?.digital_wallet;
  }

  public getEcheckInfo() {
    return this.paymentInfo.echeck_info;
  }

  public getPaypalInfo() {
    return this.paymentInfo.paypal;
  }

  public getAmazonPayInfo() {
    return this.paymentInfo.amazonpay;
  }

  public getCreditCardType() {
    return this.paymentInfo.credit_card_info?.credit_card_type;
  }

  public getIsCreditCardFromApplePayWallet() {
    return this.getDigitalWalletInfo() === PaymentMethodsEnum.APPLE_PAY;
  }

  public getCardToken() {
    return this.paymentInfo.credit_card_info?.card_token;
  }

  public getApplePayCreditCardToken() {
    if (this.getIsCreditCardFromApplePayWallet()) {
      return this.paymentInfo.credit_card_info?.card_token;
    }

    return null;
  }

  public getMaskedCreditCardNumber() {
    return this.paymentInfo.credit_card_info?.masked_credit_card_number;
  }

  public getExpirationDate() {
    return this.paymentInfo.credit_card_info?.expiration_date;
  }

  // Overview Getters
  public getPrepay() {
    return this.overview.prepay;
  }

  public getAccountEmailAddresses() {
    return this.overview.account_email_addresses;
  }

  public getContactEmailAddress() {
    return this.overview.account_email_addresses.contact_email_address;
  }

  public getCustomerInfoOnFile() {
    return this.overview.customer_info_on_file;
  }

  public getMonthlyFee() {
    return this.overview.monthly_fee;
  }

  public getPaymentMethodOnFile() {
    return this.overview.payment_method_on_file;
  }

  public getTrialEndDate() {
    return this.overview.trial_end_date;
  }

  public getEcheckAllowed() {
    return this.overview.echeck_allowed;
  }

  public getNextChargeScheduled() {
    return this.overview.next_charge_scheduled;
  }

  public getDaysLeftInPeriod() {
    return this.overview.days_left_in_period;
  }

  public getContactTier() {
    return this.overview.contact_tier;
  }

  public getAccountStatus() {
    return this.overview.account_status;
  }

  public getIsCanceled() {
    return this.getAccountStatus() === AccountStatus.Canceled;
  }

  public getIsCustomer() {
    return this.getAccountStatus() === AccountStatus.Open;
  }

  public getPaymentPeriod() {
    return this.overview.payment_period;
  }

  public getBalance() {
    return this.overview.balance;
  }

  public getPrepayBalance() {
    return this.overview.prepay_balance;
  }

  public getProjectedPrepayBalance() {
    return this.overview.projected_prepay_balance;
  }

  public getProjectedPrepayRenewal() {
    return this.overview.projected_prepay_renewal;
  }

  public getMonthsRemaining() {
    return this.overview.months_remaining;
  }

  public getPrepayNextChargeScheduled() {
    return this.overview.prepay_next_charge_scheduled;
  }

  public getAllEmails() {
    return this.overview.account_email_addresses.all_account_email_addresses;
  }

  public getUsesStripePayments() {
    return this.customerInfo.has_stripe_customer || this.paymentInfo.payment_type === 'stripe';
  }
}
